<template>
  <dashboard-panel
    title="Events"
    icon="fa-calendar-check"
    :tabs="canAccess() ? tabs : []"
    :active-tab="activeTab"
    :count="count"
    add-label="add an event"
    :add-permissions="['can_add_events']"
    filterable
    :can-export="count > 0"
    :downloading="loadingDownload"
    :adding-to-vault="loadingAddToVault"
    @download="downloadEvents"
    @add-to-vault="addToVault"
    :loading="fetching"
    :empty="!events.total"
    empty-text="No events found"
    :pager="events"
    :busy="busy"
    busy-text="Posting event, please wait..."
    @tab="selectTab"
    @add="$refs.addEvent.$refs.modal.open()"
    @page="goToPage"
  >
    <template #filter>
      <label class="block text-xxs uppercase tracking-wide text-grey-dark font-semibold mb-1">Starting date from / to</label>
      <div class="flex items-center">
        <flat-pickr
          class="appearance-none border rounded w-full h-10 px-3 text-sm"
          placeholder="Select date"
          ref="filter_start"
          :config="date_filter_start"
          v-model="filters.start_at_range"
          :events="[
            'on-change',
            'on-close',
            'on-destroy',
            'on-key-down',
            'on-month-change',
            'on-open',
            'on-parse-config',
            'on-ready',
            'on-value-update',
          ]"
        ></flat-pickr>
        <button
          type="button"
          class="ml-2 px-2 text-grey-dark hover:text-red"
          title="Clear"
          @click.prevent="$refs.filter_start.fp.clear()"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
    </template>

    <dashboard-list-item
      v-for="event in events.data"
      :key="event.id"
      :author="creatorName(event)"
      :date="event.start_at"
      :title="event.title"
      :body="event.description"
      @click="view(event)"
    >
      <template #badge>
        <span
          v-if="event.recurring != 0"
          class="flex-none rounded-full text-xxs uppercase tracking-wide text-white bg-brand py-1 px-2 ml-2"
        >
          {{ recurringText(event.recurring) }}
        </span>
      </template>
      <p class="mt-1">
        <i class="fas fa-clock mr-1 text-brand"></i>
        {{ formatDate(event.start_at, "h:mma") }}
        <span v-if="event.end_at">- {{ formatDate(event.end_at, "h:mma") }}</span>
        <span v-if="endsAnotherDay(event)">
          until {{ formatDate(event.end_at, "D MMM YYYY") }}
        </span>
      </p>
      <p
        class="mt-1 text-orange"
        v-if="isAttend(event) != false && isAttend(event) == null"
      >
        You haven't confirmed this event yet.
      </p>
    </dashboard-list-item>

    <template #modals>
      <add-event
        :user="user"
        :vault="vault"
        :vaultUsers="vaultUsers"
        @processing="processing"
        @eventAdded="eventAdded"
        ref="addEvent"
      ></add-event>

      <view-event
        :user="user"
        :role="role"
        :vault="vault"
        :vaultUsers="vaultUsers"
        :selected_event="selected_event"
        @processing="processing"
        @eventUpdated="eventUpdated"
        @eventDeleted="eventDeleted"
        @currentEvent="selected_event"
        @getEvents="getEvents"
        ref="viewEvent"
      ></view-event>
    </template>
  </dashboard-panel>
</template>

<script>
import moment from "moment";
import flatPickr from "vue-flatpickr-component";
import { mapGetters } from "vuex";
import DashboardPanel from "./DashboardPanel.vue";
import DashboardListItem from "./DashboardListItem.vue";
import AddEvent from "./Events/Add.vue";
import ViewEvent from "./Events/View.vue";

export default {
  components: {
    DashboardPanel,
    DashboardListItem,
    flatPickr,
    AddEvent,
    ViewEvent,
  },

  props: ["vault-users"],

  data() {
    return {
      events: {},
      selected_event: {},
      count: 0,
      tabs: [
        { key: "you", label: "You" },
        { key: "all", label: "All" },
        { key: "past", label: "Past" },
      ],
      busy: false,

      loadingDownload: false,
      loadingAddToVault: false,

      filters: {
        start_at_range: "",
        end_at_range: "",
      },

      date_filter_start: {
        wrap: true,
        allowInput: false,
        dateFormat: "d/m/Y",
        mode: "range",
        monthSelectorType: "static",
        onChange: (selectedDates) => {
          if (_.isEmpty(selectedDates)) {
            this.search_params.from_start_at = "";
            this.search_params.to_start_at = "";
            this.getEvents();
          }
          if (!_.isNil(selectedDates[0])) {
            this.search_params.from_start_at = moment(selectedDates[0]).format(
              "YYYY-MM-DD"
            );
          }
          if (!_.isNil(selectedDates[1])) {
            this.search_params.to_start_at = moment(selectedDates[1]).format(
              "YYYY-MM-DD"
            );
          }
        },
        onClose: (selectedDates, dateStr, instance) => {
          this.getEvents();
        },
      },

      date_filter_end: {
        wrap: true,
        allowInput: false,
        dateFormat: "d/m/Y",
        mode: "range",
        monthSelectorType: "static",
        onChange: (selectedDates) => {
          if (_.isEmpty(selectedDates)) {
            this.search_params.from_end_at = "";
            this.search_params.to_end_at = "";
            this.getEvents();
          }
          if (!_.isNil(selectedDates[0])) {
            this.search_params.from_end_at = moment(selectedDates[0]).format(
              "YYYY-MM-DD"
            );
          }
          if (!_.isNil(selectedDates[1])) {
            this.search_params.to_end_at = moment(selectedDates[1]).format(
              "YYYY-MM-DD"
            );
          }
        },
        onClose: (selectedDates, dateStr, instance) => {
          this.getEvents();
        },
      },

      fetching: false,

      search_params: {
        mine_only: 1,
        current_page: 1,
        per_page: 5,
        from_start_at: moment().day("-1").format("YYYY-MM-DD HH:mm:ss"),
        to_start_at: "",
        from_end_at: "",
        to_end_at: "",
        past_event: 0,
      },
    };
  },

  mounted() {
    this.init();
  },

  methods: {
    async init() {
      const userRole = await this.checkVaultUserRole();
      if(userRole == 'userG') {
        this.search_params.mine_only = 0;
      }

      this.getEvents();
    },
    checkVaultUserRole() {
      return axios
        .get(`/api/vaults/${this.$route.params.vault_id}/my-role`)
        .then(({ data }) => {
          if (data) {
            let vault_id = this.vault.id;
            let role_name = data.name;
            let replace_char = vault_id + "-";

            let role = _.camelCase(role_name.replace(replace_char, ""));

            return role;
          }
        });
    },
    recurringText(recurring) {
      const recurringText = ["Doesn't Repeat", "Weekly", "Monthly", "Annually"];
      return recurringText[recurring];
    },
    processing(isOpen) {
      this.busy = isOpen;
    },
    selectTab(key) {
      this.search_params.mine_only = key == "all" ? 0 : 1;
      this.search_params.past_event = key == "past" ? 1 : 0;
      this.search_params.current_page = 1;
      this.getEvents();
    },
    goToPage(page) {
      this.search_params.current_page = page;
      this.getEvents();
    },
    eventAdded() {
      this.$ebus.$emit("refresh-file-and-tag-panel");
      this.$noty.success("Event has been posted!", {
        killer: true,
        timeout: 1500,
      });
      this.$ebus.$emit("update-events-count");
      this.getEvents();
    },
    eventUpdated() {
      this.$ebus.$emit("refresh-file-and-tag-panel");
      this.$noty.success("Event has been edited!", {
        killer: true,
        timeout: 1500,
      });
      this.getEvents();
    },
    eventDeleted() {
      this.$noty.success("Event has been deleted!", {
        killer: true,
        timeout: 1500,
      });
      this.getEvents();
    },
    downloadEvents() {
      this.loadingDownload = true;
      setTimeout(() => {
        const url = `api/vaults/${this.$route.params.vault_id}/events`;
        const searchParams = new URLSearchParams({
          download: true,
          own_only: this.search_params.mine_only,
          page: this.search_params.current_page,
          items_per_page: this.search_params.per_page,
          from_start_at: this.search_params.from_start_at,
          to_start_at: this.search_params.to_start_at,
          from_end_at: this.search_params.from_end_at,
          to_end_at: this.search_params.to_end_at,
        });

        this.$noty.success(`Your download will begin shortly.`);

        window.open(`${url}?${searchParams.toString()}`, "_blank");
        this.loadingDownload = false;
      }, 500);
    },
    addToVault() {
      this.loadingAddToVault = true;
      const url = `api/vaults/${this.$route.params.vault_id}/events/add-to-vault`;
      const searchParams = new URLSearchParams({
        own_only: this.search_params.mine_only,
        page: this.search_params.current_page,
        items_per_page: this.search_params.per_page,
        from_start_at: this.search_params.from_start_at,
        to_start_at: this.search_params.to_start_at,
        from_end_at: this.search_params.from_end_at,
        to_end_at: this.search_params.to_end_at,
      });
      axios
        .post(`${url}?${searchParams.toString()}`)
        .then((response) => {
          if (response && response.data) {
            const data = response.data;

            if (data.success) {
              this.$noty.success(data.message);
              this.$ebus.$emit("refresh-file-and-tag-panel");
            } else {
              this.$noty.error(data.message);
            }
          }
        })
        .catch((error) => {
          this.$noty.error(`Failed to add Events to Vault.`);
        })
        .finally(() => {
          this.loadingAddToVault = false;
        });
    },
    getEvents() {
      this.fetching = true;
      this.$nextTick(() => {
        axios
          .get(`/api/vaults/${this.$route.params.vault_id}/events`, {
            params: {
              own_only: this.search_params.mine_only,
              page: this.search_params.current_page,
              items_per_page: this.search_params.per_page,
              from_start_at: this.search_params.from_start_at
                ? this.search_params.from_start_at
                : moment().format("YYYY-MM-DD"),
              to_start_at: this.search_params.to_start_at,
              // from_end_at: this.search_params.from_end_at,
              // to_end_at: this.search_params.to_end_at,
              past_event: this.search_params.past_event,
            },
          })
          .then(({ data }) => {
            this.count = data.total;
            this.events = data;
            this.fetching = false;
          })
          .catch((err) => {
            this.fetching = false;
          });
      });
    },

    formatDate(date, format) {
      let d = moment(date);
      return d.format(format);
    },

    endsAnotherDay(event) {
      return (
        !!event.end_at && !moment(event.end_at).isSame(event.start_at, "day")
      );
    },

    creatorName(event) {
      const user = _.find(this.vaultUsers, { id: event.user_id });
      if (!user) {
        return "-";
      }
      return `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
    },

    isAttend(event) {
      const found = event.users.find((user) => user.id == this.user.id);
      if (found) {
        return found.pivot.is_attend;
      }
      return false;
    },

    view(event) {
      this.$refs.viewEvent.$refs.modal.open();

      this.selected_event = { ...event };

      this.selected_event.start_date = this.formatDate(
        event.start_at,
        "DD/MM/YYYY HH:mm A"
      );
      if (this.selected_event.end_at) {
        this.selected_event.end_date = this.formatDate(
          event.end_at,
          "DD/MM/YYYY HH:mm A"
        );
      } else {
        this.selected_event.end_date = null;
      }

      this.$nextTick(function () {
        this.$refs.viewEvent.setAttachedUser();
        this.$refs.viewEvent.fillPostEventTo();
      });
    },

    canAccess() {
      if (_.includes(["userA", "userB", "userC", "userD", "userF"], this.role)) {
        return true;
      }
      return false;
    },
  },

  computed: {
    ...mapGetters({
      user: "getUser",
      role: "getUserRole",
      vault: "getCurrentVault",
    }),

    activeTab() {
      if (this.search_params.past_event == 1) {
        return "past";
      }
      return this.search_params.mine_only == 1 ? "you" : "all";
    },
  },

  watch: {
    "$route.params.vault_id"() {
      _.delay(() => {
        this.getEvents();
      }, 1000);
    },
  },
};
</script>
